import { useEffect, useState } from "react";
import api, { API } from "../lib/api";
import {
  FileText,
  FileSpreadsheet,
  FileJson,
  Mail,
  RefreshCw,
  Calendar,
  TrendingUp,
  TrendingDown,
  Wallet,
  Coins,
  Inbox,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) =>
  `৳${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const monthLabel = (yyyymm) => {
  if (!yyyymm) return "—";
  const [y, m] = yyyymm.split("-").map(Number);
  if (!y || !m) return yyyymm;
  return new Date(y, m - 1, 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
};

// Build an authenticated browser-side download. Files come from
// /api/agent/me/statements/<period>.<ext> which require Bearer auth, so we
// fetch as a Blob and trigger a temporary <a> click.
const authedDownload = async (path, filename) => {
  const token = localStorage.getItem("ss_agent_token");
  const resp = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    let msg = "";
    try {
      const j = await resp.json();
      msg = j.detail || "";
    } catch (_e) { /* ignore */ }
    throw new Error(msg || `Download failed (${resp.status})`);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

export default function MyStatements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState({}); // { "2026-06": "pdf"|"csv"|"json"|"email" }

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/agent/me/statements");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to load statements"
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDownload = async (period, kind) => {
    const ext = kind; // pdf | csv | json
    const key = `${period}:${kind}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      await authedDownload(
        `/agent/me/statements/${period}.${ext}`,
        `maxlive88-statement-${period}.${ext}`
      );
      toast.success(`Statement ${ext.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || `Could not download ${ext.toUpperCase()}`);
    } finally {
      setBusy((b) => {
        const { [key]: _, ...rest } = b;
        return rest;
      });
    }
  };

  const onEmail = async (period) => {
    const key = `${period}:email`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const { data } = await api.post(
        `/agent/me/statements/${period}/email`
      );
      toast.success(
        `Statement emailed to ${data.email}`
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Email re-send failed"
      );
    } finally {
      setBusy((b) => {
        const { [key]: _, ...rest } = b;
        return rest;
      });
    }
  };

  return (
    <div
      className="space-y-6"
      data-testid="agent-my-statements-page"
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-amber-400/80 font-semibold">
            Records · Premium Reports
          </div>
          <h1 className="mt-1 text-2xl md:text-3xl font-bold text-white">
            My Statements
          </h1>
          <p className="text-sm text-white/60 mt-1 max-w-2xl">
            Every monthly statement that was auto-archived for you on the 1st
            of each month. Download the PDF for printing, the CSV for Excel,
            or get the full raw JSON. You can also re-email a statement to
            your registered address with one click.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-xs font-medium text-white/90 hover:bg-white/10 disabled:opacity-60"
          data-testid="agent-my-statements-refresh"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
          />
          Refresh
        </button>
      </div>

      {/* Empty / Loading */}
      {loading ? (
        <div
          className="flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] py-16 text-white/60"
          data-testid="agent-my-statements-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading your statements…
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-xl border border-white/10 bg-white/[0.03] py-16 px-6 text-center"
          data-testid="agent-my-statements-empty"
        >
          <Inbox className="mx-auto h-10 w-10 text-white/30 mb-3" />
          <div className="text-white font-semibold">
            No archived statements yet
          </div>
          <p className="text-sm text-white/55 mt-1 max-w-md mx-auto">
            Your first statement will appear here on the 1st of next month,
            covering this month&apos;s transactions. The monthly auto-email
            includes the same PDF.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => (
            <StatementRow
              key={it.period}
              item={it}
              busy={busy}
              onDownload={onDownload}
              onEmail={onEmail}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatementRow({ item, busy, onDownload, onEmail }) {
  const s = item.summary || {};
  const period = item.period;
  const isBusy = (k) => !!busy[`${period}:${k}`];
  const anyBusy = ["pdf", "csv", "json", "email"].some(isBusy);

  return (
    <div
      className="group rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-white/[0.02] p-4 md:p-5 hover:border-amber-500/30 hover:shadow-[0_0_0_1px_rgba(245,158,11,0.18)] transition"
      data-testid={`agent-statement-row-${period}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* Left: Period + meta */}
        <div className="flex items-start gap-3 min-w-[180px]">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-amber-500/15 border border-amber-500/30">
            <Calendar className="h-5 w-5 text-amber-300" />
          </div>
          <div>
            <div
              className="text-base md:text-lg font-bold text-white leading-tight"
              data-testid={`agent-statement-period-${period}`}
            >
              {monthLabel(period)}
            </div>
            <div className="text-[11px] uppercase tracking-wider text-white/40 mt-0.5">
              Statement · {period}
            </div>
          </div>
        </div>

        {/* Middle: KPI strip */}
        <div className="flex flex-wrap items-center gap-3 md:gap-5 flex-1 min-w-[300px] justify-end md:justify-center">
          <Kpi
            icon={TrendingUp}
            tone="emerald"
            label="Deposits"
            value={fmt(s.deposit_total)}
            sub={`${s.deposit_count || 0} tx`}
          />
          <Kpi
            icon={TrendingDown}
            tone="rose"
            label="Withdrawals"
            value={fmt(s.withdraw_total)}
            sub={`${s.withdraw_count || 0} tx`}
          />
          <Kpi
            icon={Wallet}
            tone="sky"
            label="Net"
            value={fmt(s.net_total)}
            highlight
          />
          <Kpi
            icon={Coins}
            tone="amber"
            label="Commission"
            value={fmt(s.commission_total)}
          />
        </div>

        {/* Right: Action buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <ActionBtn
            icon={FileText}
            label="PDF"
            tone="amber"
            loading={isBusy("pdf")}
            disabled={anyBusy}
            onClick={() => onDownload(period, "pdf")}
            testid={`agent-statement-pdf-${period}`}
          />
          <ActionBtn
            icon={FileSpreadsheet}
            label="CSV"
            tone="emerald"
            loading={isBusy("csv")}
            disabled={anyBusy || !item.has_raw}
            title={
              item.has_raw
                ? "Excel-friendly CSV"
                : "Raw data unavailable for this period"
            }
            onClick={() => onDownload(period, "csv")}
            testid={`agent-statement-csv-${period}`}
          />
          <ActionBtn
            icon={FileJson}
            label="JSON"
            tone="sky"
            loading={isBusy("json")}
            disabled={anyBusy || !item.has_raw}
            title={
              item.has_raw
                ? "Raw JSON"
                : "Raw data unavailable for this period"
            }
            onClick={() => onDownload(period, "json")}
            testid={`agent-statement-json-${period}`}
          />
          <ActionBtn
            icon={Mail}
            label="Email me"
            tone="violet"
            loading={isBusy("email")}
            disabled={anyBusy}
            onClick={() => onEmail(period)}
            testid={`agent-statement-email-${period}`}
          />
        </div>
      </div>
    </div>
  );
}

const TONE_TEXT = {
  emerald: "text-emerald-300",
  rose: "text-rose-300",
  sky: "text-sky-300",
  amber: "text-amber-300",
  violet: "text-violet-300",
};
const TONE_BG = {
  emerald: "bg-emerald-500/15 border-emerald-500/30 hover:bg-emerald-500/25",
  rose: "bg-rose-500/15 border-rose-500/30 hover:bg-rose-500/25",
  sky: "bg-sky-500/15 border-sky-500/30 hover:bg-sky-500/25",
  amber: "bg-amber-500/15 border-amber-500/30 hover:bg-amber-500/25",
  violet: "bg-violet-500/15 border-violet-500/30 hover:bg-violet-500/25",
};

function Kpi({ icon: Icon, tone = "sky", label, value, sub, highlight }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`flex h-8 w-8 items-center justify-center rounded-md border ${TONE_BG[tone]} border-opacity-30`}
      >
        <Icon className={`h-4 w-4 ${TONE_TEXT[tone]}`} />
      </div>
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wider text-white/45 font-semibold">
          {label}
        </div>
        <div
          className={`text-sm font-bold ${
            highlight ? "text-emerald-300" : "text-white"
          }`}
        >
          {value}
        </div>
        {sub && (
          <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>
        )}
      </div>
    </div>
  );
}

function ActionBtn({
  icon: Icon,
  label,
  tone = "sky",
  loading,
  disabled,
  onClick,
  title,
  testid,
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition ${TONE_BG[tone]} ${TONE_TEXT[tone]} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Icon className="h-3.5 w-3.5" />
      )}
      {label}
    </button>
  );
}
